import React, { useState, useEffect, KeyboardEvent } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import DragDrop from "../../../components/drag-drop/DragDrop";
import { MuiLoadingButton, MyRichTextEditor, RoundedSquareIcon } from "../../../components";
import { Blog, SEO } from "../../../../core/models/db"; // Ajustar ruta
import { AddCircleOutline, Cancel, Check as CheckIcon, Close as CloseIcon } from "@mui/icons-material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import moment from "moment";

import useDebouncedCallback from "../../../../core/hooks/@debounced-callback/useDebouncedCallback";
import { title } from "process";

interface BlogFormProps {
  loading?: boolean;
  initialBlog: Blog; // Blog con o sin SEO
  checkBlogSlug?: (id: string, slug: string) => Promise<boolean>; // si necesitas chequear slug
  onSubmit: (
    blog: Blog & { seo?: SEO },
    imageItem: {
      oldImage: string;
      image: string;
      nameImage: string;
    }
  ) => void;
}

const BlogForm = ({
  loading,
  initialBlog,
  checkBlogSlug,
  onSubmit,
}: BlogFormProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(!initialBlog?.id);

  // Imagen principal
  const [image, setImage] = useState(initialBlog?.image || "");
  const [oldImage, setOldImage] = useState(initialBlog?.image || "");
  const [imageName, setImageName] = useState("");

  // Content con Quill
  const [content, setContent] = useState(initialBlog?.content || "");

  // Manejo de slug
  const [slugError, setSlugError] = useState<string | null>(null);
  const [slugChecked, setSlugChecked] = useState<null | boolean>(null);

  // Manejo de keywords
  const [keywordChips, setKeywordChips] = useState<string[]>(
    initialBlog?.seo?.keywords
      ? initialBlog.seo.keywords.split(",").map((kw) => kw.trim()).filter(Boolean)
      : []
  );
  const [newKeyword, setNewKeyword] = useState("");
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  // Validaciones
  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Título requerido").max(200, "Máximo 200 caracteres."),
    subtitle: Yup.string().max(200, "Máximo 80 caracteres."),
    seo: Yup.object().shape({
      slug: Yup.string()
        .max(50, "Máximo 50 caracteres")
        .matches(/^\/[a-z0-9]+\/[a-z0-9]+(?:-[a-z0-9]+)*$/, "Formato de slug inválido.")
        .required("El slug es obligatorio.")
        .test(
          "slug-availability",
          "Este slug ya está en uso.", // Mensaje de error personalizado
          async (value) => {
            if (slugError) return false;
            return true;
          }
        ),
      title: Yup.string().max(100, "Máximo 100 caracteres en SEO title."),
      description: Yup.string().max(300, "Máximo 300 caracteres en SEO description."),
    }),
  });

  // Formik
  const formik = useFormik({
    initialValues: {
      id: initialBlog?.id,
      title: initialBlog?.title || "",
      isValid: initialBlog?.isValid ?? true,
      subtitle: initialBlog?.subtitle || "",
      isPublished: initialBlog?.isPublished ?? false,
      startPublicationDate: initialBlog?.startPublicationDate
        ? moment(initialBlog.startPublicationDate).toDate()
        : null,
      seo: {
        id: initialBlog?.seo?.id,
        slug: initialBlog?.seo?.slug || "",
        title: initialBlog?.seo?.title || "",
        description: initialBlog?.seo?.description || "",
        keywords: initialBlog?.seo?.keywords || "",
      },
    },
    validationSchema,
    onSubmit: (values) => {
      // Pasamos content (del Quill) y keywords como string
      const finalKeywords = keywordChips.join(",");

      const blogToSave: Blog & { seo?: SEO } = {
        ...values,
        image,
        content, // Editor Quill
        startPublicationDate: values.startPublicationDate
          ? moment(values.startPublicationDate).toDate()
          : null,
        seo: {
          ...values.seo,
          keywords: finalKeywords,
        },
      };

      onSubmit(blogToSave, { oldImage, image, nameImage: values.title });
    },
    enableReinitialize: true,
  });

  // Actualizar chips si cambian
  useEffect(() => {
    if (initialBlog?.seo?.keywords) {
      const arr = initialBlog.seo.keywords.split(",").map((x) => x.trim()).filter(Boolean);
      setKeywordChips(arr);
    } else {
      setKeywordChips([]);
    }
  }, [initialBlog]);

  // Manejo de imagen
  const handleFiles = (files: any[]) => {
    if (files.length === 0) {
      setImage("");
      formik.setFieldValue("image", "");
    } else if (files?.[0].blob) {
      const file = files[0];
      const url = URL.createObjectURL(file.blob);
      setImage(url);
      formik.setFieldValue("image", url);
    }
  };

  const changeToBackup = () => {
    setImage(null);
    setImageName("");
    setTimeout(() => {
      setImage(initialBlog?.image);
      setImageName(
        initialBlog?.image?.substring(initialBlog?.image?.lastIndexOf("/") + 1)
      );
      setOldImage(initialBlog?.image);
      setContent(initialBlog?.content || "");
    }, 200);
  };

  const handleCancelClick = () => {
    formik.resetForm();
    changeToBackup();
    setIsEditing(false);
  };

  const handleEditClick = () => {
    setIsEditing(true);
  };

  // Keywords
  const handleAddKeyword = () => {
    const kwTrim = newKeyword.trim();
    if (!kwTrim) {
      setKeywordsError("La palabra clave no puede estar vacía.");
      return;
    }
    if (keywordChips.includes(kwTrim)) {
      setKeywordsError("Esta palabra clave ya está en la lista.");
      return;
    }
    if (keywordChips.length >= 10) {
      setKeywordsError("No puedes añadir más de 10 palabras clave.");
      return;
    }
    setKeywordChips((prev) => [...prev, kwTrim]);
    setNewKeyword("");
    setKeywordsError(null);
  };

  const handleRemoveKeyword = (kw: string) => {
    setKeywordChips((prev) => prev.filter((k) => k !== kw));
  };

  const handleKeywordKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  // Para slug con debounce (opcional)
  // const handleSlugValidation = useDebouncedCallback(async (value: string) => {
  //   if (!value || !checkBlogSlug) return;
  //   const used = await checkBlogSlug(initialBlog?.seo?.id || "", value);
  //   if (used) {
  //     formik.setFieldError("seo.slug", "Este slug ya está en uso.");
  //   }
  // }, 300);


  const handleSlugValidation = useDebouncedCallback(async (value: string) => {
    if (!value) return;
    const used = await checkBlogSlug(initialBlog?.seo?.id || "", value);

    setSlugChecked(!used);
    setSlugError(used ? "Este slug ya está en uso." : null);
    if (used) {
      formik.setFieldError("seo.slug", "Este slug ya está en uso.");
    } else {
      formik.setFieldError("seo.slug", undefined);
    }
  }, 300);



  return (
    <form onSubmit={formik.handleSubmit}>

      <Box>
        {/* Sección: Imagen / Datos Principales */}
        <Typography variant="h6" sx={{ mb: 1 }}>
          Datos Principales
        </Typography>
        <DragDrop
          id="blog-image"
          valueDefault={image}
          disabled={loading || !isEditing}
          directoryNameList={["blog", "images"]}
          maxSizeMB={2}
          typeAllowed={["image/*"]}
          isOnlyOneFile={true}
          isBlob={true}
          specialCase={true}
          showFileList={false}
          handleFiles={handleFiles}
        />

        <Grid container spacing={2} mt={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label="Título*"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.title && formik.errors.title)}
              helperText={
                formik.touched.title && typeof formik.errors.title === "string"
                  ? formik.errors.title
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialBlog?.id)}
              fullWidth
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              variant="standard"
              label="Subtítulo"
              name="subtitle"
              value={formik.values.subtitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={Boolean(formik.touched.subtitle && formik.errors.subtitle)}
              helperText={
                formik.touched.subtitle && typeof formik.errors.subtitle === "string"
                  ? formik.errors.subtitle
                  : undefined
              }
              disabled={loading || (!isEditing && !!initialBlog?.id)}
              fullWidth
            />
          </Grid>
        </Grid>
        {/* isValid (checkbox) */}


        {/* isPublished (checkbox) */}
        {/* <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<RoundedSquareIcon checked={false} />}
                  checkedIcon={<RoundedSquareIcon checked={true} />}
                  disabled={loading || (!isEditing && !!initialBlog?.id)}
                  name="isPublished"
                  checked={formik.values.isPublished}
                  onChange={(e) => {
                    formik.setFieldValue("isPublished", e.target.checked);
                  }}
                />
              }
              label="¿Está publicado?"
            />
          </Grid> */}
        <Grid container spacing={2} mt={2}>
          {/* startPublicationDate */}
          <Grid item xs={12} sm={6}>
            <DateTimePicker
              slotProps={{
                textField: {
                  fullWidth: true,
                  variant: "standard",
                },
              }}
              label="Fecha publicación"
              value={formik.values.startPublicationDate ? moment(formik.values.startPublicationDate) : null}
              onChange={(newValue) => {
                formik.setFieldValue("startPublicationDate", newValue ? newValue.toDate() : null);
              }}
              disabled={loading || (!isEditing && !!initialBlog?.id)}
            />
          </Grid>

          <Grid item xs={12} sm={12} style={{ display: "flex", alignItems: "flex-end" }}>
            <FormControlLabel
              control={
                <Checkbox
                  icon={<RoundedSquareIcon checked={false} />}
                  checkedIcon={<RoundedSquareIcon checked={true} />}
                  disabled={loading || (!isEditing && !!initialBlog?.id)}
                  name="isValid"
                  checked={!formik.values.isValid}
                  onChange={(e) => {
                    formik.setFieldValue("isValid", !e.target.checked);
                  }}
                />
              }
              label="Meter en Reserva"
            />
          </Grid>
        </Grid>

        {/* Sección: Contenido (con Quill) */}
        <Box mt={4}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Contenido del Blog
          </Typography>
          <MyRichTextEditor
            directoriesImagefile={["images", "blog", "content"]}
            disabled={loading || (!isEditing && !!initialBlog?.id)}
            value={content}
            onChange={(val) => setContent(val)}
          // customClassName="my-quill-class"
          />

        </Box>

        {/* Sección: SEO */}
        <Box mt={4}>
          {/* <Typography variant="h5" sx={{ mb: 2 }}>
            SEO
          </Typography> */}
          <div className="text-2xl font-medium mb-4">SEO</div>
          <Grid container spacing={2}>

            <div className="flex flex-col">
              {/* <div>
                <pre>slug error {JSON.stringify(formik?.errors?.seo?.slug, null, 2)}</pre>
              </div>
              <div>
                <pre>todos los errores {JSON.stringify(formik?.errors, null, 2)}</pre>
              </div>
              <div>
                <pre>slug checked {slugChecked}</pre>
              </div> */}
            </div>
            {/* Campo Slug */}
            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                label="Slug*"
                name="seo.slug"
                value={formik.values.seo.slug.replace(/^\/blog\//, "")} // Mostrar sin el prefijo
                onChange={(e) => {
                  let inputValue = e.target.value;

                  // Quitar el prefijo "/blog/" si el usuario lo escribe manualmente
                  inputValue = inputValue.replace(/^\/blog\//, "");

                  // Internamente almacenamos el prefijo "/blog/"
                  const prefixedValue = `/blog/${inputValue}`;
                  formik.setFieldValue("seo.slug", prefixedValue);

                  console.log("prefixedValue", prefixedValue);
                  // Validar el slug con debounce (si es necesario)
                  handleSlugValidation(prefixedValue);
                }}
                onBlur={(e) => {
                  let inputValue = formik.values.seo.slug;

                  // Asegurar que el prefijo "/blog/" siempre esté presente
                  if (!inputValue.startsWith("/blog/")) {
                    inputValue = `/blog/${inputValue}`;
                  }

                  formik.setFieldValue("seo.slug", inputValue);
                  formik.handleBlur(e);
                }}
                error={Boolean(formik.touched.seo?.slug && formik.errors.seo?.slug)}
                helperText={
                  formik.touched.seo?.slug && typeof formik.errors.seo?.slug === "string"
                    ? formik.errors.seo?.slug
                    : "Identificador único en la URL, como '/mi-titulo-seo'."
                }
                disabled={loading || (!isEditing && !!initialBlog?.id)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography variant="body2" color="textSecondary">
                        /blog/
                      </Typography>
                    </InputAdornment>
                  ),
                  endAdornment: (

                    <InputAdornment position="end">

                      {isEditing && slugChecked !== null && (

                        <IconButton edge="end">
                          {slugChecked && !formik?.errors?.seo?.slug ? (
                            <CheckIcon color="success" />
                          ) : (
                            <CloseIcon color="error" />
                          )}
                        </IconButton>
                      )}

                    </InputAdornment>

                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                variant="standard"
                label="SEO Title"
                name="seo.title"
                value={formik.values.seo.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading || (!isEditing && !!initialBlog?.id)}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                variant="standard"
                label="SEO Description"
                name="seo.description"
                value={formik.values.seo.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={loading || (!isEditing && !!initialBlog?.id)}
                multiline
                rows={2}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Palabras clave (Keywords)
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  variant="standard"
                  label="Nueva palabra clave"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyDown={handleKeywordKeyDown}
                  disabled={loading || (!isEditing && !!initialBlog?.id)}
                />
                <div className="flex items-end mt-2.5">
                  <IconButton
                    color="primary"
                    onClick={handleAddKeyword}
                    disabled={!newKeyword.trim() || loading || (!isEditing && !!initialBlog?.id)}
                  >
                    <AddCircleOutline />
                  </IconButton>
                </div>
              </Box>
              <Box mt={1} display="flex" flexWrap="wrap" gap={1}>
                {keywordChips.map((kw) => (
                  <Chip
                    key={kw}
                    label={kw}
                    onDelete={() => handleRemoveKeyword(kw)}
                    deleteIcon={<Cancel />}
                    disabled={loading || (!isEditing && !!initialBlog?.id)}
                  />
                ))}
              </Box>
              {keywordsError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {keywordsError}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Botones */}
        {/* BOTONES DE ACCIÓN */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={5}>
          {!initialBlog?.id && (
            <div className="w-auto">
              <MuiLoadingButton
                title="Crear"
                variant="contained"
                color="primary"
                type="submit"
                disabled={!formik.isValid || loading || slugChecked === false}
                isLoading={loading}
              />
            </div>
          )}
          {initialBlog?.id && !isEditing && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleEditClick}
              disabled={loading}
            >
              Editar
            </Button>
          )}
          {initialBlog?.id && isEditing && (
            <div className="flex flex-row gap-2">
              <Button
                className="flex-1"
                variant="outlined"
                color="primary"
                onClick={handleCancelClick}
                disabled={loading}
              >
                Cancelar
              </Button>
              <div className="w-auto">
                <MuiLoadingButton
                  title="Guardar"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!formik.isValid || loading || slugChecked === false}
                  isLoading={loading}
                />
              </div>
            </div>
          )}
        </Box>
      </Box>
    </form >
  );
};

export default BlogForm;
